import { CheckedStatus, ControlledFilter, FilterNode, SelectionBehaviorVariants } from './model';

export const toPathId = (paths: string[]) => paths.join(' > ');

export const getCheckedStatus = (
    nodes: ControlledFilter[],
    selectionBehavior?: SelectionBehaviorVariants,
): CheckedStatus =>
    (selectionBehavior !== 'fuzzy' && nodes?.every((node) => node.checked === true)) ||
    (nodes?.some((node) => !!node.checked) && 'indeterminate') ||
    false;

export const toControlledTree = (
    nodes: FilterNode[],
    pathFromRoot: string[] = [],
    selected: string[] = [],
    selectionBehavior?: SelectionBehaviorVariants,
    searchWord?: string,
): ControlledFilter[] => {
    return nodes?.map((node) => {
        const currentPath = [...pathFromRoot, node.title];
        const pathId = toPathId(currentPath);
        const filters = toControlledTree(node?.filters, currentPath, selected, selectionBehavior, searchWord);

        return {
            ...node,
            visible: !searchWord || includesSearchWord(node, searchWord),
            checked: selected.includes(pathId) || getCheckedStatus(filters, selectionBehavior),
            pathFromRoot: currentPath,
            pathId,
            filters,
        };
    });
};

export const updateTreeCheckedStatusToRoot = (nodes: ControlledFilter[]): ControlledFilter[] =>
    nodes?.map((node) => {
        const filters = updateTreeCheckedStatusToRoot(node.filters);

        return { ...node, filters, checked: node.filters?.length ? getCheckedStatus(filters) : !!node.checked };
    });

export const updateTreeCheckedStatusToRootFuzzyBehavior = (
    nodes: ControlledFilter[],
    isParentChecked = false,
    lastSelectedPathId?: string,
): ControlledFilter[] =>
    nodes?.map((node) => {
        const filters = updateTreeCheckedStatusToRootFuzzyBehavior(
            node.filters,
            isParentChecked || lastSelectedPathId === node.pathId,
            lastSelectedPathId,
        );
        const checked = !isParentChecked ? getCheckedStatus(filters, 'fuzzy') || node.checked === true : false;

        return {
            ...node,
            filters,
            checked,
        };
    });

export const updateTreeCheckedStatusToRootSingleBehavior = (
    nodes: ControlledFilter[],
    selectedPathId: string,
): ControlledFilter[] =>
    nodes?.map((node) => {
        const filters = updateTreeCheckedStatusToRootSingleBehavior(node.filters, selectedPathId);

        return { ...node, filters, checked: node.pathId === selectedPathId || getCheckedStatus(filters, 'single') };
    });

export const updateTreeCheckedStatusToLeaf = (nodes: ControlledFilter[], checked: CheckedStatus) =>
    nodes?.forEach((node) => {
        node.checked = checked;
        updateTreeCheckedStatusToLeaf(node.filters, checked);
    });

export const extractChecked = (nodes: ControlledFilter[]) => {
    const queue = [...nodes];
    const result: string[][] = [];
    while (queue.length) {
        const node = queue.shift();
        if (node.checked === true) {
            result.push(node.pathFromRoot);
        }
        if (node.filters?.length) {
            queue.unshift(...node.filters);
        }
    }

    return result;
};

const includesSearchWord = (node: FilterNode, searchWord: string) => {
    const includes = node.title.trim().toLowerCase().includes(searchWord);

    if (includes) {
        return true;
    }
    if (!node.filters?.length) {
        return false;
    }

    return node.filters.some((child) => includesSearchWord(child, searchWord));
};
